/**
 * Based on the `useIsFocused` hook from `react-navigation` library.
 * @see: https://github.com/react-navigation/react-navigation/blob/main/packages/core/src/useIsFocused.tsx
 */

import { useNavigation } from 'expo-router'
import { useEffect, useRef, useState } from 'react'

/**
 * Hook to get the current focus state of the screen. Returns a `true` if screen is focused, otherwise `false`.
 * This can be used if a component needs to render something based on the focus state.
 */
export function useIsFocused(): boolean {
  const navigation = useNavigation()
  const [isFocused, setIsFocused] = useState(navigation.isFocused)

  useEffect(() => {
    const unsubscribeFocus = navigation.addListener('focus', () =>
      setIsFocused(true)
    )

    const unsubscribeBlur = navigation.addListener('blur', () =>
      setIsFocused(false)
    )

    return () => {
      unsubscribeFocus()
      unsubscribeBlur()
    }
  }, [navigation])

  return isFocused
}

export const useIsFocusedRef = () => {
  const navigation = useNavigation()
  const isFocused = navigation.isFocused()
  const ref = useRef(isFocused)

  useEffect(() => {
    ref.current = isFocused
  }, [isFocused])

  useEffect(() => {
    const unsubscribeFocus = navigation.addListener(
      'focus',
      () => (ref.current = true)
    )

    const unsubscribeBlur = navigation.addListener(
      'blur',
      () => (ref.current = false)
    )

    return () => {
      unsubscribeFocus()
      unsubscribeBlur()
    }
  }, [navigation])

  return ref
}

export const useOnScreenFocus = (callback: (isFocused: boolean) => void) => {
  const navigation = useNavigation()

  useEffect(() => {
    const unsubscribeFocus = navigation.addListener('focus', () =>
      callback(true)
    )

    const unsubscribeBlur = navigation.addListener('blur', () =>
      callback(false)
    )

    return () => {
      unsubscribeFocus()
      unsubscribeBlur()
    }
  }, [navigation, callback])
}
